// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Party planner + Catch Pass  (the "picks" a customer builds: catch → cut + packs of each size)
// Shared by components/catch/CatchPicker, pages/PartyPlanner and pages/CatchPass. Prices are the menu's (data/seafood + data/packs +
// the cut's prep fee) — no bulk discount exists, so none is applied. A pick is per catch: ONE cut, 500 g and 1 kg pack counts.
// ─────────────────────────────────────────────────────────────────────────────
import { cutOf, defaultCut, prepFee, type CutId } from "../data/cuts";
import { PACK_SIZES, packPrice, type PackId } from "../data/packs";
import { seafoodItems, type SeafoodItem } from "../data/seafood";
import type { PlanLine } from "./plans";

export interface Pick {
  cut: CutId;
  "500g": number;
  "1kg": number;
}
export type Picks = Record<string, Pick>;

export const MAX_PER_SIZE = 10;

export const pickOf = (picks: Picks, item: SeafoodItem): Pick => picks[item.id] ?? { cut: defaultCut(item).id, "500g": 0, "1kg": 0 };

export const packsIn = (pick: Pick) => pick["500g"] + pick["1kg"];

/** Rupees for one catch's picks (packs + the cut's prep fee per pack). */
export function pickPrice(item: SeafoodItem, pick: Pick): number {
  const cut = cutOf(item, pick.cut);
  return PACK_SIZES.reduce((sum, pack) => sum + pick[pack.id] * (packPrice(item, pack) + prepFee(cut, pack)), 0);
}

export const pickKg = (pick: Pick) => pick["500g"] * 0.5 + pick["1kg"];

export function totals(picks: Picks): { kg: number; price: number; packs: number } {
  let kg = 0;
  let price = 0;
  let packs = 0;
  for (const item of seafoodItems) {
    const pick = picks[item.id];
    if (!pick) continue;
    kg += pickKg(pick);
    price += pickPrice(item, pick);
    packs += packsIn(pick);
  }
  return { kg, price, packs };
}

/** Picks → the plan API's lines (only catches with at least one pack). */
export function picksToLines(picks: Picks): PlanLine[] {
  return seafoodItems.flatMap((item) => {
    const pick = picks[item.id];
    if (!pick) return [];
    return PACK_SIZES.filter((pack) => pick[pack.id] > 0).map((pack) => ({ catchId: item.id, pack: pack.id as PackId, qty: pick[pack.id], cut: pick.cut }));
  });
}

/** A plan's lines → picks (unknown catches — no longer on the menu — are dropped). */
export function linesToPicks(lines: PlanLine[]): Picks {
  const picks: Picks = {};
  for (const line of lines) {
    const item = seafoodItems.find((candidate) => candidate.id === line.catchId);
    if (!item) continue;
    const pick = picks[item.id] ?? { cut: cutOf(item, line.cut ?? undefined).id, "500g": 0, "1kg": 0 };
    pick[line.pack] = Math.min(MAX_PER_SIZE, pick[line.pack] + line.qty);
    picks[item.id] = pick;
  }
  return picks;
}

/** A short "2 × 500 g Tiger Prawns · 1 × 1 kg …" summary. */
export function summarise(lines: PlanLine[]): string[] {
  return lines.flatMap((line) => {
    const item = seafoodItems.find((candidate) => candidate.id === line.catchId);
    if (!item) return [];
    const cut = cutOf(item, line.cut ?? undefined);
    return [`${line.qty} × ${line.pack === "1kg" ? "1 kg" : "500 g"} ${item.name} · ${cut.name}`];
  });
}

/** What a saved plan costs at today's menu prices. */
export const linesPrice = (lines: PlanLine[]) => totals(linesToPicks(lines)).price;
